import { z } from 'zod';
import { DAY_KEEPS, MARKET_DAY_SOURCES, TRADED_VENUES, VENUES } from '../../ontology/calendar.ts';
import { IMPORTANCES } from '../../ontology/economic.ts';
import { STOCK_EVENT_KINDS } from '../../ontology/stock-events.ts';
import { lineSchema } from './copy.ts';
import { day, instant } from './primitives.ts';

const clock = z.string().regex(/^\d{2}:\d{2}$/);

/** One stock, kind and day, NSE's words joined, as `stockDays` says it. */
const stockDaySchema = z.object({
  day,
  symbol: z.string(),
  kind: z.enum(STOCK_EVENT_KINDS),
  said: lineSchema,
  /** The circulars it came of, joined; null for what NSE's files say. */
  reference: z.string().nullable(),
});

/** Only what was published: the browser builds sessions with the server's own `marketCalendar`. */
export const calendarSchema = z.object({
  days: z.array(
    z.object({
      venue: z.enum(VENUES),
      day,
      keep: z.enum(DAY_KEEPS),
      name: z.string(),
      windows: z.array(z.object({ opens: clock, closes: clock })),
      source: z.enum(MARKET_DAY_SOURCES),
      reference: z.string().nullable(),
    }),
  ),
  events: z.array(
    z.object({
      day,
      economy: z.string(),
      name: z.string(),
      importance: z.enum(IMPORTANCES),
      previous: z.string().nullable(),
      actual: z.string().nullable(),
      unit: z.string(),
      at: instant.nullable(),
    }),
  ),
  expiries: z.array(
    z.object({
      contract: z.string(),
      venue: z.enum(TRADED_VENUES),
      expiry: day,
      weekly: z.boolean(),
    }),
  ),
  /** Each expiry, and each day a tender period starts, said as Home's Today says it. */
  contracts: z.array(z.object({ day, said: lineSchema })),
  stocks: z.object({
    /** The reader's held and followed names; null for a reader with no member account. */
    yours: z.array(stockDaySchema.extend({ own: z.boolean(), href: z.string() })).nullable(),
    /** Every ban and circular on file for the other names; the desk's carries their results too. */
    others: z.array(stockDaySchema),
  }),
});

export type CalendarRead = z.infer<typeof calendarSchema>;
